// frontend/src/components/TeamProfileEditor.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    PlusIcon,
    GripIcon,
    DeleteIcon,
    SaveIcon,
    TextFieldsIcon,
    CloudQueueIcon,
    BarChartIcon,
    InsertPhotoIcon,
    PieChartIcon
} from './Icons';

import TextBlockEditor from './ProfileBlockEditors/TextBlockEditor';
import WordCloudEditor from './ProfileBlockEditors/WordCloudEditor';
import StatsBlockEditor from './ProfileBlockEditors/StatsBlockEditor';
import BarsBlockEditor from './ProfileBlockEditors/BarsBlockEditor';
import ImageBlockEditor from './ProfileBlockEditors/ImageBlockEditor';

const BLOCK_TYPES = [
    { value: 'text', label: 'Text', icon: <TextFieldsIcon fontSize="small" /> },
    { value: 'wordcloud', label: 'Wortwolke', icon: <CloudQueueIcon fontSize="small" /> },
    { value: 'stats', label: 'Zahlen & Fakten', icon: <PieChartIcon fontSize="small" /> },
    { value: 'bars', label: 'Balkendiagramm', icon: <BarChartIcon fontSize="small" /> },
    { value: 'image', label: 'Bild', icon: <InsertPhotoIcon fontSize="small" /> },
];

const TeamProfileEditor = ({
    teamMemberId,
    onSave,
    blocks: controlledBlocks,
    setBlocks: setControlledBlocks,
    hideSaveButton = false
}) => {
    // Determine if we are in controlled mode
    const isControlled = controlledBlocks !== undefined && setControlledBlocks !== undefined;

    // Internal state if not controlled
    const [internalBlocks, setInternalBlocks] = useState([]);

    // Resolve what to use
    const blocks = isControlled ? controlledBlocks : internalBlocks;
    const setBlocks = isControlled ? setControlledBlocks : setInternalBlocks;

    const [loading, setLoading] = useState(!isControlled); // Only load if we are fetching data ourselves
    const [saving, setSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);

    useEffect(() => {
        // Only fetch if not controlled
        if (!isControlled && teamMemberId) {
            fetchBlocks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMemberId, isControlled]);

    const fetchBlocks = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/team/${teamMemberId}`);
            if (response.data.profileBlocks) {
                const sortedBlocks = response.data.profileBlocks.sort((a, b) => a.order - b.order);
                setBlocks(sortedBlocks);
            }
        } catch (error) {
            console.error('Error fetching profile blocks:', error);
        } finally {
            setLoading(false);
        }
    };

    const addBlock = (type) => {
        const newBlock = {
            type,
            order: blocks.length,
            content: getDefaultContent(type),
            _isNew: true // Mark as new for frontend
        };
        setBlocks([...blocks, newBlock]);
    };

    const getDefaultContent = (type) => {
        switch (type) {
            case 'text':
                return { title: '', text: '' };
            case 'wordcloud':
                return { title: '', words: [] };
            case 'stats':
                return { title: '', items: [] };
            case 'bars':
                return { title: '', items: [] };
            case 'image':
                return { title: '', imageUrl: '', caption: '' };
            default:
                return {};
        }
    };

    const updateBlock = (index, updatedBlock) => {
        const newBlocks = [...blocks];
        newBlocks[index] = updatedBlock;
        setBlocks(newBlocks);
    };

    const removeBlock = (index) => {
        setBlocks(blocks.filter((_, i) => i !== index));
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newBlocks = [...blocks];
        const draggedBlock = newBlocks[draggedIndex];
        newBlocks.splice(draggedIndex, 1);
        newBlocks.splice(index, 0, draggedBlock);

        setBlocks(newBlocks);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        // Update order after drag ends
        const reorderedBlocks = blocks.map((block, index) => ({
            ...block,
            order: index
        }));
        setBlocks(reorderedBlocks);
    };

    const handleSave = async () => {
        setSaving(true);
        const storedUser = localStorage.getItem('user');
        const token = storedUser ? JSON.parse(storedUser).token : null;

        try {
            // Clean up temporary fields
            const cleanedBlocks = blocks.map(({ _isNew, ...block }) => block);

            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/team/${teamMemberId}`,
                { profileBlocks: cleanedBlocks },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (onSave) onSave();
            alert('Profilseite erfolgreich gespeichert!');
        } catch (error) {
            console.error('Error saving profile blocks:', error);
            alert('Fehler beim Speichern: ' + (error.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    const renderBlockEditor = (block, index) => {
        const commonProps = {
            block,
            onChange: (updatedBlock) => updateBlock(index, updatedBlock),
            teamMemberId
        };

        switch (block.type) {
            case 'text':
                return <TextBlockEditor {...commonProps} />;
            case 'wordcloud':
                return <WordCloudEditor {...commonProps} />;
            case 'stats':
                return <StatsBlockEditor {...commonProps} />;
            case 'bars':
                return <BarsBlockEditor {...commonProps} />;
            case 'image':
                return <ImageBlockEditor {...commonProps} />;
            default:
                return <div className="p-4 text-error bg-error-container rounded">Unbekannter Block-Typ</div>;
        }
    };

    if (loading) {
        return <div className="text-body-medium text-surface-onVariant">Lädt...</div>;
    }

    return (
        <div className="flex flex-col gap-6">
            {!isControlled && (
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-title-large font-bold text-surface-on">Profilseite gestalten</h2>
                    {!hideSaveButton && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="md3-btn-filled"
                        >
                            {saving ? 'Speichert...' : <>
                                <SaveIcon className="mr-2" /> Speichern
                            </>}
                        </button>
                    )}
                </div>
            )}

            {/* Blocks List */}
            <div className="flex flex-col gap-4">
                {blocks.length === 0 ? (
                    <div className="p-8 text-center border-2 border-dashed border-outlineVariant rounded-xl bg-surface-containerLow">
                        <p className="text-body-large text-surface-onVariant">
                            Noch keine Blöcke vorhanden. Füge Blöcke hinzu, um deine Profilseite zu gestalten!
                        </p>
                    </div>
                ) : (
                    blocks.map((block, index) => (
                        <div
                            key={index}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`
                                rounded-xl border border-outlineVariant bg-surface transition-all duration-200
                                ${draggedIndex === index ? 'opacity-50 ring-2 ring-primary' : 'hover:shadow-elevation-1'}
                            `}
                        >
                            {/* Header / Drag Handle */}
                            <div className="flex items-center justify-between p-3 border-b border-outlineVariant bg-surface-containerLow rounded-t-xl">
                                <div className="flex items-center gap-3">
                                    <div
                                        className="cursor-grab text-surface-onVariant active:cursor-grabbing p-1 hover:bg-surface-on/10 rounded"
                                    >
                                        <GripIcon />
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1 bg-secondary-container text-secondary-onContainer rounded-full text-label-medium font-bold">
                                        {BLOCK_TYPES.find(t => t.value === block.type)?.icon}
                                        <span>{BLOCK_TYPES.find(t => t.value === block.type)?.label || block.type}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeBlock(index)}
                                    className="md3-icon-btn text-error hover:bg-error-container hover:text-error-on-container w-8 h-8"
                                    title="Block entfernen"
                                >
                                    <DeleteIcon fontSize="small" />
                                </button>
                            </div>

                            {/* Editor Content */}
                            <div className="p-4">
                                {renderBlockEditor(block, index)}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add Block Buttons */}
            <div className="p-4 border border-dashed border-outline rounded-xl bg-surface-container">
                <h3 className="text-title-small font-bold text-surface-on mb-3">Neuen Block hinzufügen</h3>
                <div className="flex flex-wrap gap-2">
                    {BLOCK_TYPES.map((type) => (
                        <button
                            key={type.value}
                            onClick={() => addBlock(type.value)}
                            className="md3-btn-outlined border-outline bg-surface hover:bg-primary-container hover:border-primary hover:text-primary-onContainer transition-colors"
                        >
                            {type.icon}
                            {type.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TeamProfileEditor;
